// Analytics Dashboard Page
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ConfirmationNumber,
  Article,
  Lightbulb,
  People,
  AccessTime,
  CheckCircle,
  Schedule,
  Visibility,
} from '@mui/icons-material';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ticketsService } from '@/services/collections/tickets';
import { articlesService } from '@/services/collections/articles';
import { featuresService } from '@/services/collections/features';
import type { Ticket, Article as ArticleType, FeatureRequest } from '@/types';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color: string;
}

const StatCard = ({ title, value, subtitle, icon, trend, color }: StatCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
      {trend !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
          {trend >= 0 ? (
            <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
          ) : (
            <TrendingDown sx={{ fontSize: 18, color: 'error.main' }} />
          )}
          <Typography
            variant="caption"
            sx={{ color: trend >= 0 ? 'success.main' : 'error.main', fontWeight: 'medium' }}
          >
            {trend >= 0 ? '+' : ''}{trend}% from last period
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

interface StatusBreakdownProps {
  title: string;
  data: { label: string; value: number; color: string }[];
  total: number;
}

const StatusBreakdown = ({ title, data, total }: StatusBreakdownProps) => (
  <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
    <Typography variant="h6" fontWeight="medium" gutterBottom>
      {title}
    </Typography>
    <Box sx={{ mt: 2 }}>
      {data.map((item) => {
        const percentage = total > 0 ? (item.value / total) * 100 : 0;
        return (
          <Box key={item.label} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{item.label}</Typography>
              <Typography variant="body2" fontWeight="medium">
                {item.value} ({percentage.toFixed(0)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(item.color, 0.2),
                '& .MuiLinearProgress-bar': {
                  bgcolor: item.color,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  </Paper>
);

export const AnalyticsDashboardPage = () => {
  const { organization, project } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  // Data state
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [articles, setArticles] = useState<ArticleType[]>([]);
  const [features, setFeatures] = useState<FeatureRequest[]>([]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!organization || !project) return;

      setLoading(true);
      try {
        const [ticketsData, articlesData, featuresData] = await Promise.all([
          ticketsService.getByProject(organization.id, project.id),
          articlesService.getPublished(organization.id, project.id),
          featuresService.getByProject(organization.id, project.id),
        ]);

        setTickets(ticketsData);
        setArticles(articlesData);
        setFeatures(featuresData);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, project]);

  // Calculate stats
  const daysAgo = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  };

  const filterByDate = <T extends { createdAt?: { toDate: () => Date } }>(
    items: T[],
    days: number
  ): T[] => {
    const cutoff = daysAgo(days);
    return items.filter((item) => {
      if (!item.createdAt?.toDate) return false;
      return item.createdAt.toDate() >= cutoff;
    });
  };

  const range = parseInt(timeRange);
  const recentTickets = filterByDate(tickets, range);
  const previousTickets = filterByDate(tickets, range * 2).filter(
    (t) => !recentTickets.includes(t)
  );

  // Ticket stats
  const ticketStats = {
    total: tickets.length,
    recent: recentTickets.length,
    open: tickets.filter((t) => ['new', 'open', 'pending'].includes(t.status)).length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    avgResponseTime: (() => {
      const respondedTickets = tickets.filter((t) => t.firstResponseAt && t.createdAt);
      if (respondedTickets.length === 0) return 'N/A';
      const totalMs = respondedTickets.reduce((sum, t) => {
        const created = t.createdAt?.toMillis?.() || 0;
        const responded = t.firstResponseAt?.toMillis?.() || 0;
        return sum + (responded - created);
      }, 0);
      const avgHours = totalMs / respondedTickets.length / (1000 * 60 * 60);
      return avgHours < 1 ? `${Math.round(avgHours * 60)}m` : `${avgHours.toFixed(1)}h`;
    })()
  };

  const ticketTrend =
    previousTickets.length > 0
      ? Math.round(((recentTickets.length - previousTickets.length) / previousTickets.length) * 100)
      : 0;

  // Ticket status breakdown
  const ticketStatusData = [
    { label: 'New', value: tickets.filter((t) => t.status === 'new').length, color: '#3b82f6' },
    { label: 'Open', value: tickets.filter((t) => t.status === 'open').length, color: '#f59e0b' },
    { label: 'Pending', value: tickets.filter((t) => t.status === 'pending').length, color: '#8b5cf6' },
    { label: 'Resolved', value: tickets.filter((t) => t.status === 'resolved').length, color: '#10b981' },
    { label: 'Closed', value: tickets.filter((t) => t.status === 'closed').length, color: '#94a3b8' },
  ];

  // Ticket type breakdown
  const ticketTypeData = [
    { label: 'Support', value: tickets.filter((t) => t.type === 'support').length, color: '#3b82f6' },
    { label: 'Bug', value: tickets.filter((t) => t.type === 'bug').length, color: '#ef4444' },
    { label: 'Feature Request', value: tickets.filter((t) => t.type === 'feature_request').length, color: '#f59e0b' },
    { label: 'Feedback', value: tickets.filter((t) => t.type === 'feedback').length, color: '#8b5cf6' },
    { label: 'Question', value: tickets.filter((t) => t.type === 'question').length, color: '#10b981' },
  ];

  // Article stats
  const totalViews = articles.reduce((sum, a) => sum + (a.viewCount || 0), 0);
  const totalHelpful = articles.reduce((sum, a) => sum + (a.helpfulCount || 0), 0);
  const totalNotHelpful = articles.reduce((sum, a) => sum + (a.notHelpfulCount || 0), 0);
  const helpfulRate = totalHelpful + totalNotHelpful > 0
    ? Math.round((totalHelpful / (totalHelpful + totalNotHelpful)) * 100)
    : 0;

  // Feature stats
  const featureStats = {
    total: features.length,
    totalVotes: features.reduce((sum, f) => sum + (f.voteCount || 0), 0),
    planned: features.filter((f) => f.status === 'planned').length,
    inProgress: features.filter((f) => f.status === 'in_progress').length,
    released: features.filter((f) => f.status === 'released').length,
  };

  const featureStatusData = [
    { label: 'Under Review', value: features.filter((f) => f.status === 'under_review').length, color: '#94a3b8' },
    { label: 'Planned', value: features.filter((f) => f.status === 'planned').length, color: '#3b82f6' },
    { label: 'In Progress', value: features.filter((f) => f.status === 'in_progress').length, color: '#f59e0b' },
    { label: 'Released', value: features.filter((f) => f.status === 'released').length, color: '#10b981' },
    { label: 'Declined', value: features.filter((f) => f.status === 'declined').length, color: '#ef4444' },
  ];

  // Top articles
  const topArticles = [...articles]
    .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
    .slice(0, 5);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your support performance and customer engagement
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Total Tickets"
            value={ticketStats.total}
            subtitle={`${ticketStats.recent} in last ${range} days`}
            icon={<ConfirmationNumber />}
            trend={ticketTrend}
            color="#3b82f6"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Open Tickets"
            value={ticketStats.open}
            subtitle="Awaiting response"
            icon={<Schedule />}
            color="#f59e0b"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="KB Articles"
            value={articles.length}
            subtitle={`${totalViews.toLocaleString()} total views`}
            icon={<Article />}
            color="#8b5cf6"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Feature Requests"
            value={featureStats.total}
            subtitle={`${featureStats.totalVotes} total votes`}
            icon={<Lightbulb />}
            color="#10b981"
          />
        </Grid>
      </Grid>

      {/* Ticket Breakdowns */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StatusBreakdown
            title="Tickets by Status"
            data={ticketStatusData}
            total={tickets.length}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <StatusBreakdown
            title="Tickets by Type"
            data={ticketTypeData}
            total={tickets.length}
          />
        </Grid>
      </Grid>

      {/* Knowledge Base & Features */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              Top Knowledge Base Articles
            </Typography>
            {topArticles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No articles published yet
              </Typography>
            ) : (
              <Box sx={{ mt: 2 }}>
                {topArticles.map((article, index) => (
                  <Box
                    key={article.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: index < topArticles.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        mr: 2,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap fontWeight="medium">
                        {article.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.category || 'Uncategorized'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                      <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {article.viewCount || 0}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {articles.length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{helpfulRate}%</strong> of readers found articles helpful
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <StatusBreakdown
            title="Feature Requests by Status"
            data={featureStatusData}
            total={features.length}
          />
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {ticketStats.resolved}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Resolved Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {ticketStats.avgResponseTime}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Response Time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Lightbulb sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {featureStats.planned + featureStats.inProgress}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Features in Pipeline
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {new Set(tickets.map((t) => t.submitterEmail)).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Submitters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
